import type { LinksFunction, LoaderFunction, MetaFunction } from "@remix-run/node"
import {
  Link,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useRouteError,
  useRouteLoaderData,
} from "@remix-run/react"
import { captureRemixErrorBoundaryError } from "@sentry/remix"
import { cx } from "class-variance-authority"
import { Toaster } from "sonner"
import { PodpodLogo } from "./packages/podpod-logo"
import { getSession } from "./session.server"
import "./tailwind.css"
import { useMedia } from "./packages/use-media"

export const links: LinksFunction = () => [
  // Font preconnects and stylesheet
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:ital,wght@0,100..900;1,100..900&display=swap",
  },
  // Favicon and manifest links
  {
    rel: "icon",
    type: "image/png",
    href: "/favicon-96x96.png",
    sizes: "96x96",
  },
  {
    rel: "icon",
    type: "image/svg+xml",
    href: "/favicon.svg",
  },
  {
    rel: "shortcut icon",
    href: "/favicon.ico",
  },
  {
    rel: "apple-touch-icon",
    sizes: "180x180",
    href: "/apple-touch-icon.png",
  },
  {
    rel: "manifest",
    href: "/site.webmanifest",
  },
]

export const meta: MetaFunction = () => {
  return [
    { title: "Podpod" },
    { name: "description", content: "Podpod, a podcast made just for you." },
    { name: "apple-mobile-web-app-title", content: "Podpod" },
    { property: "og:image", content: "https://podpod.me/podpod-og-image.jpg" }, // Added OG image
  ]
}

export const loader: LoaderFunction = async ({ request }) => {
  const session = await getSession(request)
  const colorScheme = session.getColorScheme()
  return { colorScheme }
}

export function Layout({ children }: { children: React.ReactNode }) {
  const data = useRouteLoaderData<typeof loader>("root")
  const isDark = useMedia("(prefers-color-scheme: dark)")
  const colorSchemeClassName =
    data?.colorScheme === "system" ? (isDark ? "dark" : "light") : data?.colorScheme

  return (
    <html lang="en" className={cx("bg-podpod-bg text-gray-12", colorSchemeClassName)}>
      <head>
        <script
          defer
          src="https://cloud.umami.is/script.js"
          data-website-id="d3a197a5-7389-4320-b64c-6371ea7458d5"
          data-domains="podpod.me"
        />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <Toaster />
        <div className="isolate">{children}</div>
        <ScrollRestoration />
        <Scripts />
        <ClientThemeScript />
      </body>
    </html>
  )
}

export default function App() {
  return <Outlet />
}

export const ErrorBoundary = () => {
  const error = useRouteError()
  captureRemixErrorBoundaryError(error)
  return (
    <div className="w-full h-dvh flex flex-col items-center justify-center text-center">
      <div className="flex flex-col gap-4">
        <PodpodLogo className="mx-auto" />

        <div className="flex flex-col gap-y-1">
          <h2 className="text-sm">Something wend wrong</h2>
          <Link to="/" className="text-xs text-gray-9">
            Back to home
          </Link>
        </div>
      </div>
    </div>
  )
}

const ClientThemeScript = () => (
  <script
    dangerouslySetInnerHTML={{
      __html: generateScopedJS(
        `const { matches } = matchMedia("(prefers-color-scheme: dark)")`,
        `const { classList } = document.documentElement`,
        `classList.toggle('light', !matches)`,
        `classList.toggle('dark', matches)`
      ),
    }}
  />
)
const generateScopedJS = (...linesOfCode: string[]) => `{${linesOfCode.join(";")}}`
